(function (root, factory) {
  const createPageTrackingPageView = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = createPageTrackingPageView;
    return;
  }

  if (root) {
    root.createPageTrackingPageView = createPageTrackingPageView;
  }
})(typeof window !== 'undefined' ? window : undefined, function createPageTrackingPageViewFactory() {
  return function createPageTrackingPageView(core, options) {
    const currentOptions = options || {};
    const pageIdentity = currentOptions.pageIdentity || {
      pageName: '',
      subPageName: ''
    };
    const heartbeatStorageKeyPrefix = 'pageTrackingHeartbeatLastSentAt:';
    const heartbeatIntervalMs = 60 * 1000;
    const state = core.state;

    function buildPageViewData(scrollDepth, now) {
      return core.buildDataInfo({
        type: core.config.eventTypes && core.config.eventTypes.pageView,
        pageName: pageIdentity.pageName,
        subPageName: pageIdentity.subPageName,
        scrollDepth: scrollDepth
      }, now);
    }

    function getCurrentScrollDepth() {
      const documentElement = core.document.documentElement || {};
      const body = core.document.body || {};
      const scrollTop = core.window.scrollY || documentElement.scrollTop || body.scrollTop || 0;
      const viewportHeight = core.window.innerHeight || documentElement.clientHeight || 0;
      const pageHeight = Math.max(
        body.scrollHeight || 0,
        body.offsetHeight || 0,
        documentElement.clientHeight || 0,
        documentElement.scrollHeight || 0,
        documentElement.offsetHeight || 0
      );

      if (!pageHeight || pageHeight <= viewportHeight) {
        return 100;
      }

      return Math.min(100, Math.round(((scrollTop + viewportHeight) / pageHeight) * 100));
    }

    function resetPageSessionState() {
      const currentScrollDepth = getCurrentScrollDepth();

      state.initialExposureSent = false;
      state.maxScrollDepth = currentScrollDepth;
      state.reportedScrollDepths = [];
    }

    function syncInitialScrollDepth() {
      state.maxScrollDepth = getCurrentScrollDepth();
    }

    function recordThresholdsCoveredByDepth(viewportDepth) {
      const depthValue = Number(viewportDepth) || 0;
      const milestoneDepths = [25, 50, 75, 100];

      milestoneDepths.forEach(function recordMilestone(depth) {
        if (depthValue >= depth && state.reportedScrollDepths.indexOf(depth) === -1) {
          state.reportedScrollDepths.push(depth);
        }
      });
    }

    function trackInitialPageViewExposure() {
      let initialDepth;
      let now;

      if (state.initialExposureSent || !core.canTrack()) {
        return false;
      }

      state.initialExposureSent = true;
      now = new Date();
      state.maxScrollDepth = Math.max(state.maxScrollDepth, getCurrentScrollDepth());
      initialDepth = state.maxScrollDepth;
      recordThresholdsCoveredByDepth(initialDepth);

      return core.sendTrackingEvent({
        basicInfo: core.buildBasicInfo(),
        dataInfo: buildPageViewData(initialDepth, now)
      }, true);
    }

    function scheduleInitialPageViewExposure() {
      function runExposure() {
        trackInitialPageViewExposure();
      }

      function deferWithDoubleAnimationFrame() {
        if (typeof core.window.requestAnimationFrame === 'function') {
          core.window.requestAnimationFrame(function scheduleSecondFrame() {
            core.window.requestAnimationFrame(runExposure);
          });
        } else {
          core.window.setTimeout(runExposure, 0);
        }
      }

      if (core.document.readyState === 'complete' || core.document.readyState === 'interactive') {
        deferWithDoubleAnimationFrame();
        return;
      }

      core.document.addEventListener(
        'readystatechange',
        function handleReadyStateChange() {
          if (core.document.readyState === 'loading') {
            return;
          }

          core.document.removeEventListener('readystatechange', handleReadyStateChange);
          deferWithDoubleAnimationFrame();
        }
      );
    }

    function handleScroll(sessionModule, sessionCallbacks) {
      const nowMs = Date.now();
      const currentSessionCallbacks = sessionCallbacks || {};

      sessionModule.ensureActiveSession(nowMs, currentSessionCallbacks);

      const newlyReachedDepths = core.utils.getReachedScrollDepths({
        maxScrollDepth: Math.max(state.maxScrollDepth, getCurrentScrollDepth()),
        reportedDepths: state.reportedScrollDepths
      });

      state.maxScrollDepth = Math.max(state.maxScrollDepth, getCurrentScrollDepth());

      newlyReachedDepths.forEach(function sendDepthEvent(depth) {
        state.reportedScrollDepths.push(depth);
        core.sendTrackingEvent({
          basicInfo: core.buildBasicInfo(),
          dataInfo: buildPageViewData(depth, new Date())
        }, true);
      });

      core.markActivity(nowMs);
    }

    function handlePageExit(savePendingPageJump) {
      if (typeof savePendingPageJump === 'function') {
        savePendingPageJump();
      }
    }

    function getHeartbeatStorageKey() {
      const sessionId = core.getSessionId();

      return heartbeatStorageKeyPrefix + (sessionId || 'anonymous');
    }

    function getHeartbeatScheduleAnchorMs() {
      const lastHeartbeatAt = Number(core.readSessionStorage(getHeartbeatStorageKey())) || 0;

      if (lastHeartbeatAt) {
        return lastHeartbeatAt;
      }

      const sessionStartAt = core.getSessionStartAt();

      if (sessionStartAt) {
        return sessionStartAt;
      }

      return Date.now();
    }

    function getHeartbeatDelayMs() {
      const nowMs = Date.now();
      const anchorMs = getHeartbeatScheduleAnchorMs();

      return Math.max(0, heartbeatIntervalMs - (nowMs - anchorMs));
    }

    function scheduleSessionHeartbeat() {
      const delayMs = getHeartbeatDelayMs();

      core.window.setTimeout(function handleInitialHeartbeatTick() {
        sendHeartbeat();
        core.window.setInterval(sendHeartbeat, heartbeatIntervalMs);
      }, delayMs);
    }

    function sendHeartbeat() {
      const heartbeatType = core.config.eventTypes && core.config.eventTypes.heartbeat;
      const heartbeatStorageKey = getHeartbeatStorageKey();
      const now = new Date();
      const nowMs = now.getTime();
      const lastHeartbeatAt = Number(core.readSessionStorage(heartbeatStorageKey)) || 0;

      if (!heartbeatType || core.document.visibilityState === 'hidden') {
        return false;
      }

      if (lastHeartbeatAt && nowMs - lastHeartbeatAt < heartbeatIntervalMs) {
        return false;
      }

      core.writeSessionStorage(heartbeatStorageKey, String(nowMs));

      return core.sendTrackingEvent(
        {
          basicInfo: core.buildBasicInfo(),
          dataInfo: core.buildDataInfo(
            {
              type: heartbeatType,
              pageName: pageIdentity.pageName,
              subPageName: pageIdentity.subPageName,
            },
            now
          )
        },
        true
      );
    }

    function bindLifecycleEvents(sessionModule, pageJumpModule) {
      const sessionCallbacks = {
        resetPageSessionState,
        scheduleInitialPageViewExposure,
        syncInitialScrollDepth
      };
      const savePendingPageJump = pageJumpModule && pageJumpModule.savePendingPageJump;

      core.window.addEventListener('scroll', function handleWindowScroll() {
        handleScroll(sessionModule, sessionCallbacks);
      }, { passive: true });

      core.window.addEventListener('click', function handleClick() {
        const nowMs = Date.now();

        sessionModule.ensureActiveSession(nowMs, sessionCallbacks);
        core.markActivity(nowMs);
      }, { passive: true });

      core.window.addEventListener('keydown', function handleKeydown() {
        const nowMs = Date.now();

        sessionModule.ensureActiveSession(nowMs, sessionCallbacks);
        core.markActivity(nowMs);
      });

      core.window.addEventListener('touchstart', function handleTouchstart() {
        const nowMs = Date.now();

        sessionModule.ensureActiveSession(nowMs, sessionCallbacks);
        core.markActivity(nowMs);
      }, { passive: true });

      core.window.addEventListener('pagehide', function handlePagehide() {
        handlePageExit(savePendingPageJump);
      });

      core.window.addEventListener('beforeunload', function handleBeforeunload() {
        handlePageExit(savePendingPageJump);
      });

      core.document.addEventListener('visibilitychange', function handleVisibilitychange() {
        if (core.document.visibilityState === 'hidden' && typeof savePendingPageJump === 'function') {
          savePendingPageJump();
        }
      });

      scheduleSessionHeartbeat();
    }

    return {
      buildPageViewData,
      getCurrentScrollDepth,
      resetPageSessionState,
      syncInitialScrollDepth,
      recordThresholdsCoveredByDepth,
      trackInitialPageViewExposure,
      scheduleInitialPageViewExposure,
      handleScroll,
      handlePageExit,
      sendHeartbeat,
      bindLifecycleEvents
    };
  };
});
